import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateOpportunity } from "@/hooks/use-opportunities";
import { MOVABLE_STAGES, STAGE_LABELS } from "@/lib/opportunity-calc";
import type { OpportunityStage } from "@/lib/db-types";

interface NewOpportunityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
}

/** Ny prosess på en kunde. Starter i Outreach eller Møte; Tilbud kommer fra tilbudet. */
export default function NewOpportunityDialog({ open, onOpenChange, customerId, customerName }: NewOpportunityDialogProps) {
  const create = useCreateOpportunity();
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("");
  const [stage, setStage] = useState<Extract<OpportunityStage, "outreach" | "meeting">>("outreach");

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setTheme("");
    setStage("outreach");
  }, [open]);

  const valid = title.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ny prosess</DialogTitle>
          <DialogDescription>En salgsmulighet hos {customerName}. Du blir eier.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            create.mutate(
              { customer_id: customerId, title, theme, stage },
              { onSuccess: () => onOpenChange(false) },
            );
          }}
        >
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-opportunity-title">Tittel</Label>
              <Input id="new-opportunity-title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-opportunity-theme">Tema (valgfritt)</Label>
              <Input id="new-opportunity-theme" value={theme} onChange={(e) => setTheme(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-opportunity-stage">Fase</Label>
              <Select value={stage} onValueChange={(v) => setStage(v as typeof stage)}>
                <SelectTrigger id="new-opportunity-stage" aria-label="Fase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOVABLE_STAGES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STAGE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? "Oppretter ..." : "Opprett prosess"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
